import { Timestamp } from 'firebase/firestore';

type TimestampLike = Pick<Timestamp, 'seconds' | 'nanoseconds'>;

export class FirestoreListSyncUtil {
  private isPlainObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' &&
    value !== null &&
    (value.constructor === Object || Object.getPrototypeOf(value) === null);

  /**
   * Prefer duck-typing over `instanceof Timestamp` alone: Firebase modular
   * bundling / Vitest module graphs can load more than one Timestamp class
   * identity, so `instanceof` alone misses real timestamp-shaped values.
   */
  private isFirestoreTimestamp = (value: unknown): value is TimestampLike => {
    if (!value || typeof value !== 'object') return false;
    if (value instanceof Timestamp) return true;
    const candidate = value as Partial<TimestampLike> & { toMillis?: unknown };
    return (
      typeof candidate.seconds === 'number' &&
      typeof candidate.nanoseconds === 'number' &&
      typeof candidate.toMillis === 'function'
    );
  };

  public serializeTimestamps = <T>(value: T): T => {
    if (this.isFirestoreTimestamp(value)) {
      return { _seconds: value.seconds, _nanoseconds: value.nanoseconds } as unknown as T;
    }

    if (Array.isArray(value)) {
      return value.map(this.serializeTimestamps) as unknown as T;
    }

    if (this.isPlainObject(value)) {
      return Object.fromEntries(
        Object.entries(value).map(([key, entry]) => [key, this.serializeTimestamps(entry)]),
      ) as unknown as T;
    }

    return value;
  };
}
